import { useState, useRef, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  alpha,
} from '@mui/material'
import {
  Description as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Code as XmlIcon,
} from '@mui/icons-material'

interface DropzoneProps {
  onFileSelect: (file: File) => void
  isUploading?: boolean
  uploadProgress?: number
  accept?: string
}

export function DocumentDropzone({
  onFileSelect,
  isUploading = false,
  uploadProgress = 0,
  accept = '.pdf,.png,.jpg,.jpeg,.xml'
}: DropzoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const validateFile = (file: File): boolean => {
    // Check file extension
    const validExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.xml']
    const extension = '.' + file.name.split('.').pop()?.toLowerCase()

    if (!validExtensions.includes(extension)) {
      setError(`Invalid file type. Please upload: PDF, PNG, JPG, or XML`)
      return false
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      setError(`File too large. Maximum size is 10MB.`)
      return false
    }

    setError(null)
    return true
  }

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    if (isUploading) return

    const file = e.dataTransfer.files[0]
    if (file && validateFile(file)) {
      onFileSelect(file)
    }
  }, [isUploading, onFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!isUploading) {
      setIsDragging(true)
    }
  }, [isUploading])

  const handleDragLeave = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && validateFile(file)) {
      onFileSelect(file)
    }
    // Reset input
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }, [onFileSelect])

  const getFileIcon = () => {
    if (isUploading) return null
    return <FileIcon sx={{ fontSize: 48 }} />
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Upload Invoice Document
      </Typography>

      <Paper
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          p: 4,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'divider',
          bgcolor: isDragging ? alpha('#1976d2', 0.05) : 'background.paper',
          cursor: isUploading ? 'wait' : 'pointer',
          transition: 'all 0.2s',
          '&:hover': !isUploading ? {
            borderColor: 'primary.main',
            bgcolor: alpha('#1976d2', 0.02),
          } : {},
        }}
        onClick={() => !isUploading && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          disabled={isUploading}
        />

        {isUploading ? (
          <Stack spacing={2} alignItems="center">
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <CircularProgress size={64} variant="determinate" value={uploadProgress} />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {Math.round(uploadProgress)}%
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" fontWeight={500}>
              Uploading document...
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Please wait while we process your file
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                bgcolor: isDragging ? 'primary.main' : 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isDragging ? 'white' : 'inherit',
              }}
            >
              {getFileIcon()}
            </Box>

            <Typography variant="h6">
              Drop invoice here
            </Typography>
            <Typography variant="body2" color="textSecondary">
              or click to browse files
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" sx={{ mt: 1 }}>
              <Chip icon={<PdfIcon />} label="PDF" />
              <Chip icon={<ImageIcon />} label="PNG/JPG" />
              <Chip icon={<XmlIcon />} label="ZUGFeRD XML" />
            </Stack>

            <Typography variant="caption" color="textSecondary">
              Maximum file size: 10MB
            </Typography>
          </Stack>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="caption">
          <strong>Supported formats:</strong> PDF (digital & scanned), PNG, JPG, ZUGFeRD/XRechnung XML
        </Typography>
      </Alert>
    </Box>
  )
}

// Helper component for chips
function Chip({ icon, label }: { icon: React.ReactElement; label: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.5,
        py: 0.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        fontSize: '0.75rem',
      }}
    >
      {icon}
      <Typography variant="caption">{label}</Typography>
    </Box>
  )
}
